// Full catalog of TCGs shown in the category strip, More Games dropdown,
// mobile drawer, browse filter panel, and Post-a-Buy-Order form.
//
// Only PRIMARY_TCGS map to real DB enum values via PRIMARY_TO_CANONICAL.
// Everything in POPULAR_TCGS stores as "other" in the database, so filtering
// to those labels intentionally shows the empty state.

export const PRIMARY_TCGS = [
  "Pokémon",
  "One Piece",
  "Magic: The Gathering",
  "Riftbound",
  "Yu-Gi-Oh!",
  "Star Wars: Unlimited",
  "Disney Lorcana",
  "Digimon",
  "Flesh and Blood",
] as const;

// All remaining supported games, alphabetical (case-insensitive).
export const POPULAR_TCGS = [
  "Akora TCG",
  "Alpha Clash",
  "Argent Saga TCG",
  "BAKUGAN TCG",
  "Battle Spirits Saga",
  "Cardfight Vanguard",
  "Chrono Clash System",
  "CookieRun Braverse TCG",
  "Dragon Ball Super Fusion World",
  "Dragon Ball Super Masters",
  "Dragon Ball Z TCG",
  "Elestrals",
  "Exodus TCG",
  "Final Fantasy TCG",
  "Force of Will",
  "Future Card Buddyfight",
  "Gate Ruler",
  "Godzilla Card Game",
  "Grand Archive TCG",
  "Gundam Card Game",
  "HeroClix",
  "hololive Official Card Game",
  "Keyforge",
  "Kryptik",
  "Lightseekers TCG",
  "MetaZoo",
  "Munchkin CCG",
  "My Little Pony CCG",
  "Shadowverse Evolve",
  "Sorcery Contested Realm",
  "Star Wars Destiny",
  "The Caster Chronicles",
  "Transformers TCG",
  "Union Arena",
  "UniVersus",
  "Warhammer Age of Sigmar Champions",
  "Weiss Schwarz",
  "WIXOSS",
  "World of Warcraft TCG",
  "Zombie World Order TCG",
] as const;

// Every supported label (primary + more), for the mobile drawer's flat
// alphabetical list.
export const ALL_TCGS_ALPHABETICAL: readonly string[] = [
  ...PRIMARY_TCGS,
  ...POPULAR_TCGS,
]
  .slice()
  .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

// Short labels for the desktop primary strip (space is tight).
export const PRIMARY_SHORT: Record<(typeof PRIMARY_TCGS)[number], string> = {
  "Pokémon": "Pokémon",
  "One Piece": "One Piece",
  "Magic: The Gathering": "Magic",
  "Riftbound": "Riftbound",
  "Yu-Gi-Oh!": "Yu-Gi-Oh!",
  "Star Wars: Unlimited": "SW: Unlimited",
  "Disney Lorcana": "Lorcana",
  "Digimon": "Digimon",
  "Flesh and Blood": "Flesh & Blood",
};

// Canonical DB label a primary game filters against. `null` = no matching
// DB enum, so the browse page will render the empty state.
export const PRIMARY_TO_CANONICAL: Record<(typeof PRIMARY_TCGS)[number], string | null> = {
  "Pokémon": "Pokémon",
  "One Piece": "One Piece",
  "Magic: The Gathering": "Magic: The Gathering",
  "Riftbound": null,
  "Yu-Gi-Oh!": "Yu-Gi-Oh",
  "Star Wars: Unlimited": null,
  "Disney Lorcana": "Lorcana",
  "Digimon": "Digimon",
  "Flesh and Blood": null,
};

// Convert an entered label to the string used in the URL `tcg` search param.
export function tcgToParam(label: string): string {
  const primaryCanonical = (PRIMARY_TO_CANONICAL as Record<string, string | null>)[label];
  if (primaryCanonical !== undefined) return primaryCanonical ?? label;
  return label;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sports categories (parallel to TCGs). A "Sports" order is stored as
// tcg='other' + sport=<one of these labels>. Browse filters compare the URL
// tcg param against these labels directly, so the sport name IS the filter.
// ─────────────────────────────────────────────────────────────────────────────

export const POPULAR_SPORTS = [
  "American Football",
  "Baseball",
  "Basketball",
  "Hockey",
  "MMA/UFC",
  "Soccer",
] as const;

export const OTHER_SPORTS = [
  "BKFC",
  "Boxing",
  "Cricket",
  "Formula 1 / Racing",
  "Golf",
  "NASCAR",
  "Olympics",
  "Rugby",
  "Tennis",
  "Wrestling",
  "Multi-Sport / Other",
] as const;

export const ALL_SPORTS: readonly string[] = [...POPULAR_SPORTS, ...OTHER_SPORTS];

export function isSportLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  return (ALL_SPORTS as readonly string[]).includes(label);
}
